/**
 * UTM-tagging helper for outbound links from plinkromatic to other
 * Kromatic-owned properties.
 *
 * Per the workspace UTM convention
 * (kromatic-project/docs/utm-tracking-convention.md) — outbound links from
 * Kromatic-controlled surfaces must carry UTM parameters so GA4 can
 * attribute cross-property traffic.
 *
 * Defaults:
 *   utm_source   = "plinkromatic"
 *   utm_medium   = caller-supplied (cta | nav | footer | inline | tool_result)
 *   utm_campaign = caller-supplied (page slug / tool name) or "inline-link"
 *   utm_content  = caller-supplied (variant) — optional
 *
 * Behavior:
 *   - Only tags links whose host is kromatic.com or any *.kromatic.com
 *     subdomain (including staging.kromatic.com). External hosts are
 *     returned unchanged.
 *   - Requires an absolute URL with an explicit scheme + host. Relative
 *     URLs (e.g. "/privacy-policy") are returned unchanged — callers
 *     must resolve to an absolute kromatic.com URL first (see
 *     `resolveKromaticSiteUrl`).
 *   - Preserves any existing query string on the URL.
 *   - Does not double-tag: if the URL already carries a `utm_source`
 *     parameter, the URL is returned unchanged. All existing `utm_*`
 *     parameters (medium, campaign, content, term, id, etc.) are
 *     preserved as-is — none are overwritten.
 *   - Returns the input unchanged on parse failure or empty input.
 *
 * This helper is dependency-free and portable to sibling games-and-tools
 * properties — change DEFAULT_SOURCE per app when copying.
 */

export type UtmMedium = 'cta' | 'nav' | 'footer' | 'inline' | 'tool_result';

export interface WithUtmOptions {
    /** Marketing-medium category. Required — placement-specific. */
    medium: UtmMedium;
    /** Content identifier — typically the page slug or tool name. */
    campaign?: string;
    /** Variant identifier — optional (A/B, version, link sub-label). */
    content?: string;
    /** Override the default source (defaults to "plinkromatic"). */
    source?: string;
}

const DEFAULT_SOURCE = 'plinkromatic';
const DEFAULT_CAMPAIGN = 'inline-link';

/**
 * Returns true when the URL targets a kromatic.com path (root host or any
 * subdomain). Non-kromatic.com hosts should NOT be tagged.
 *
 * Requires an absolute URL with an explicit scheme + host. Relative URLs
 * (e.g. "/privacy-policy") return false — `withUtm`'s contract is
 * "absolute in, absolute out", so callers must resolve to an absolute
 * kromatic.com URL (via `resolveKromaticSiteUrl`) before tagging.
 */
export function isKromaticUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();
        return host === 'kromatic.com' || host.endsWith('.kromatic.com');
    } catch {
        return false;
    }
}

/**
 * Append UTM parameters to a Kromatic-owned outbound URL. Non-Kromatic
 * URLs are returned unchanged. URLs that already carry a `utm_source`
 * parameter are also returned unchanged (no double-tagging).
 */
export function withUtm(url: string, options: WithUtmOptions): string {
    if (!url || typeof url !== 'string') return url;
    if (!isKromaticUrl(url)) return url;

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return url;
    }

    if (parsed.searchParams.has('utm_source')) return url;

    const source = options.source ?? DEFAULT_SOURCE;
    const campaign = options.campaign ?? DEFAULT_CAMPAIGN;

    // Only set a parameter that is not already present — a URL can carry a
    // pre-embedded utm_campaign (or other utm_*) without utm_source (e.g.
    // RSB_URL's launch-campaign tag). Unconditional .set() here would
    // silently overwrite it, contradicting the "preserved as-is" contract
    // documented above.
    parsed.searchParams.set('utm_source', source);
    if (!parsed.searchParams.has('utm_medium')) {
        parsed.searchParams.set('utm_medium', options.medium);
    }
    if (!parsed.searchParams.has('utm_campaign')) {
        parsed.searchParams.set('utm_campaign', campaign);
    }
    if (options.content && !parsed.searchParams.has('utm_content')) {
        parsed.searchParams.set('utm_content', options.content);
    }

    return parsed.toString();
}
